import React from 'react';
import Text from '@/components/Text';
import { SlideProps } from './slideTypes';
import AssetImage from '@/components/AssetImage';
import { SLIDE_COMPONENT_TEXT_SIZE, SLIDE_COMPONENT_TITLE_SIZE } from './slideConstants';

export function RatioChartSlide({ slide }: SlideProps) {
  // Ratio chart - scatter plot with labeled circles
  const xData = Array.isArray(slide.data?.x) ? slide.data.x.filter((v: any) => v != null) : [];
  const yData = Array.isArray(slide.data?.y) ? slide.data.y.filter((v: any) => v != null) : [];
  const labels = Array.isArray(slide.data?.labels) ? slide.data.labels : [];
  const tickers = Array.isArray(slide.data?.tickers) ? slide.data.tickers : [];
  const xLabel = slide.data?.xLabel || 'P/E Ratio';
  const yLabel = slide.data?.yLabel || 'Market Cap ($B)';
  const chartTitle = slide.data?.title || xLabel;

  const minLength = Math.min(xData.length, yData.length);
  const hasValidData = minLength > 0;

  if (!hasValidData) return null;

  // Calculate ranges with padding
  const dataMinX = Math.min(...xData);
  const dataMaxX = Math.max(...xData);
  const dataMinY = Math.min(...yData);
  const dataMaxY = Math.max(...yData);
  
  const xRange = dataMaxX - dataMinX;
  const yRange = dataMaxY - dataMinY;
  
  const minX = Math.max(0, dataMinX - xRange * 0.3);
  const maxX = dataMaxX + xRange * 0.3;
  const minY = Math.max(0, dataMinY - yRange * 0.3);
  const maxY = dataMaxY + yRange * 0.3;

  const normalize = (value: number, min: number, max: number) => {
    if (max === min) return 50;
    return ((value - min) / (max - min)) * 100;
  };

  const CIRCLE_COLORS = ['#9123FF', '#FF1058', '#F4B400', '#00C608'];

  return (
    <div className="flex flex-col gap-4 md:gap-6 w-full px-4 md:px-6 py-6 md:py-8 items-start justify-center overflow-y-auto max-h-full">
      <Text size={SLIDE_COMPONENT_TITLE_SIZE} className="text-white font-normal mb-1 md:mb-2 text-left text-sm md:text-lg">{chartTitle}</Text>
      
      <div className="w-full max-w-sm aspect-square bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 relative flex flex-col items-center justify-center backdrop-blur-sm overflow-visible">
        {/* Y-Axis Label */}
        <div className="absolute -left-8 md:-left-10 top-1/2 -translate-y-1/2 -rotate-90 origin-center pointer-events-none">
          <Text size="xs" className="text-white/40 font-normal uppercase tracking-widest whitespace-nowrap text-[8px] md:text-xs">
              {yLabel}
            </Text>
          </div>

        {/* Chart Area */}
        <div className="w-full h-full relative border-l-2 border-b-2 border-white/20 ml-2 md:ml-4 mb-2 md:mb-4">
          {/* Grid lines */}
          {[0, 33, 66, 100].map((pos) => (
            <React.Fragment key={pos}>
              <div 
                className="absolute w-full h-px bg-white/5" 
                style={{ bottom: `${pos}%` }} 
              />
              <div 
                className="absolute h-full w-px bg-white/5" 
                style={{ left: `${pos}%` }} 
              />
            </React.Fragment>
          ))}

          {/* Data Points */}
          {xData.map((xVal: number, idx: number) => {
            const yVal = yData[idx];
            const label = labels[idx] || '';
            const ticker = tickers[idx] || (label && label.length <= 5 ? label.toUpperCase() : label.substring(0, 2).toUpperCase());
            
            const xPos = normalize(xVal, minX, maxX);
            const yPos = normalize(yVal, minY, maxY);
            
            // Scaled circles for mobile
            const circleSize = (typeof window !== 'undefined' && window.innerWidth < 768 ? 30 : 40) + ((yVal - dataMinY) / (dataMaxY - dataMinY || 1)) * 20;
            const circleColor = CIRCLE_COLORS[idx % CIRCLE_COLORS.length];

            return (
              <div
                key={idx}
                className="absolute -translate-x-1/2 translate-y-1/2 flex items-center justify-center transition-transform hover:scale-110"
                style={{ 
                  left: `${xPos}%`, 
                  bottom: `${yPos}%`,
                  width: circleSize,
                  height: circleSize,
                }}
              >
                <div 
                  className="rounded-full flex items-center justify-center shadow-lg border border-white/20"
                  style={{ 
                    backgroundColor: circleColor,
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Text size="xs" className="text-white font-normal text-center px-0.5 text-[8px] md:text-sm">
                    {ticker}
                  </Text>
                </div>
              </div>
            );
          })}
          </div>

        {/* X-Axis Label */}
        <div className="absolute -bottom-6 md:-bottom-8 left-1/2 -translate-x-1/2 pointer-events-none">
          <Text size="xs" className="text-white/40 font-normal uppercase tracking-widest whitespace-nowrap text-[8px] md:text-xs">
            {xLabel}
          </Text>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 md:gap-4 justify-start mt-2 md:mt-4 w-full">
        {xData.map((_, idx) => {
          const label = labels[idx] || '';
          const ticker = tickers[idx] || (label && label.length <= 5 ? label.toUpperCase() : null);
          if (!ticker) return null;

          return (
            <div key={idx} className="flex items-center gap-1.5 md:gap-2 bg-white/5 px-2 md:px-3 py-1 md:py-1.5 rounded-full border border-white/5">
              <AssetImage alt={ticker} src="" className="w-4 h-4 md:w-6 md:h-6 rounded-lg" />
              <Text size={SLIDE_COMPONENT_TEXT_SIZE} className="text-white font-normal text-[10px] md:text-md">{ticker}</Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}
